import React, {useEffect, useRef} from "react";
import {useTheme} from "@material-ui/core/styles";

interface Props {
  className?: string;
  size?: number;
}

const DefaultLogoExhibitor: React.FC<Props> = ({className, size = 48}) => {
  const theme = useTheme?.();
  const reactUseId = (React as any).useId as (() => string) | undefined;
  const baseId = reactUseId
    ? reactUseId().replace(/:/g, "")
    : React.useMemo(() => Math.random().toString(36).slice(2, 9), []);
  const clip0 = `clip0_${baseId}`;
  const clip1 = `clip1_${baseId}`;

  const pathExhibitorRef = useRef<SVGPathElement>(null);
  const pathAvatarRef = useRef<SVGPathElement>(null);

  const fillColor = theme?.palette?.primary?.main ?? "#2979FF";
  const strokeColor = theme?.palette?.primary?.main ?? "#2979FF";

  const numericSize = size || 48;

  useEffect(() => {
    const refs = [pathAvatarRef, pathExhibitorRef];

    for (const ref of refs) {
      if (ref.current) {
        ref.current.style.setProperty("fill", fillColor, "important");
      }
    }
  }, [fillColor, pathExhibitorRef]);

  return (
    <svg
      width={numericSize}
      height={numericSize}
      viewBox="0 0 434 434"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label="Default logo exhibitor"
      style={{display: "block", flex: "0 0 auto"}}
    >
      <rect width="434" height="434" rx="66.7692" fill="#FAFAFA" />
      <g clipPath={`url(#${clip0})`}>
        <g clipPath={`url(#${clip1})`}>
          <rect
            x="105.729"
            y="105.716"
            width="222.564"
            height="155.795"
            rx="22.2571"
            fill="#fff"
          />
          <path
            ref={pathAvatarRef}
            d="M169.728 176.064C177.645 176.064 183.988 169.673 183.988 161.756C183.988 153.839 177.645 147.448 169.728 147.448C161.811 147.448 155.421 153.839 155.421 161.756C155.421 169.673 161.811 176.064 169.728 176.064ZM169.728 185.602C158.616 185.602 136.344 191.182 136.344 202.294V219.782H203.113V202.294C203.113 191.182 180.841 185.602 169.728 185.602Z"
            fill={fillColor}
          />
          <rect
            width="77.8974"
            height="11.1282"
            transform="translate(219.807 141.88)"
            fill={fillColor}
          />
          <rect
            width="77.8974"
            height="11.1282"
            transform="translate(219.807 164.139)"
            fill={fillColor}
          />
          <rect
            width="77.8974"
            height="11.1282"
            transform="translate(219.807 186.394)"
            fill={fillColor}
          />
          <rect
            width="77.8974"
            height="11.1282"
            transform="translate(219.807 208.653)"
            fill={fillColor}
          />
        </g>

        <rect
          x="111.293"
          y="111.281"
          width="211.436"
          height="144.666"
          rx="16.6928"
          stroke={strokeColor}
          strokeWidth="11.1285"
          fill="none"
        />

        <path
          ref={pathExhibitorRef}
          d="M117.803 306.728V314.518H128.264V319.459H117.803V327.694H129.6V332.769H111.571V301.654H129.6V306.728H117.803ZM151.11 332.769L146.036 325.113L141.54 332.769H134.862L142.92 320.394L134.773 308.108H141.807L146.837 315.72L151.377 308.108H158.054L149.953 320.394L158.143 332.769H151.11ZM177.642 307.752C179.512 307.752 181.174 308.168 182.628 308.998C184.082 309.8 185.21 311.002 186.011 312.604C186.842 314.177 187.257 316.076 187.257 318.302V332.769H181.025V319.148C181.025 317.189 180.536 315.69 179.556 314.652C178.577 313.583 177.242 313.049 175.55 313.049C173.829 313.049 172.464 313.583 171.455 314.652C170.476 315.69 169.986 317.189 169.986 319.148V332.769H163.754V299.829H169.986V311.18C170.787 310.111 171.856 309.28 173.191 308.687C174.526 308.064 176.01 307.752 177.642 307.752ZM198.57 305.17C197.472 305.17 196.552 304.829 195.81 304.146C195.098 303.434 194.742 302.559 194.742 301.52C194.742 300.481 195.098 299.621 195.81 298.938C196.552 298.226 197.472 297.87 198.57 297.87C199.668 297.87 200.573 298.226 201.285 298.938C202.027 299.621 202.398 300.481 202.398 301.52C202.398 302.559 202.027 303.434 201.285 304.146C200.573 304.829 199.668 305.17 198.57 305.17ZM201.641 308.108V332.769H195.409V308.108H201.641ZM216.256 311.714C217.058 310.527 218.156 309.562 219.55 308.82C220.975 308.079 222.592 307.708 224.402 307.708C226.509 307.708 228.409 308.227 230.1 309.266C231.821 310.304 233.172 311.788 234.151 313.717C235.16 315.616 235.664 317.827 235.664 320.35C235.664 322.872 235.16 325.113 234.151 327.071C233.172 329 231.821 330.499 230.1 331.567C228.409 332.636 226.509 333.17 224.402 333.17C222.562 333.17 220.945 332.814 219.55 332.101C218.185 331.359 217.087 330.41 216.256 329.252V332.769H210.024V299.829H216.256V311.714ZM229.299 320.35C229.299 318.866 228.987 317.59 228.364 316.521C227.771 315.423 226.969 314.592 225.96 314.029C224.981 313.465 223.913 313.183 222.755 313.183C221.628 313.183 220.559 313.48 219.55 314.073C218.571 314.637 217.77 315.468 217.147 316.566C216.553 317.664 216.256 318.955 216.256 320.439C216.256 321.922 216.553 323.213 217.147 324.311C217.77 325.409 218.571 326.255 219.55 326.849C220.559 327.413 221.628 327.694 222.755 327.694C223.913 327.694 224.981 327.398 225.96 326.804C226.969 326.211 227.771 325.365 228.364 324.267C228.987 323.169 229.299 321.863 229.299 320.35ZM245.579 305.17C244.481 305.17 243.561 304.829 242.819 304.146C242.107 303.434 241.751 302.559 241.751 301.52C241.751 300.481 242.107 299.621 242.819 298.938C243.561 298.226 244.481 297.87 245.579 297.87C246.677 297.87 247.582 298.226 248.295 298.938C249.037 299.621 249.408 300.481 249.408 301.52C249.408 302.559 249.037 303.434 248.295 304.146C247.582 304.829 246.677 305.17 245.579 305.17ZM248.651 308.108V332.769H242.419V308.108H248.651ZM264.29 313.227V325.157C264.29 325.988 264.482 326.596 264.868 326.982C265.284 327.338 265.966 327.516 266.916 327.516H269.809V332.769H265.892C260.639 332.769 258.013 330.217 258.013 325.113V313.227H255.075V308.108H258.013V302.01H264.29V308.108H269.809V313.227H264.29ZM287.468 333.17C285.094 333.17 282.957 332.65 281.058 331.612C279.159 330.543 277.66 329.045 276.562 327.116C275.494 325.187 274.959 322.961 274.959 320.439C274.959 317.916 275.508 315.69 276.606 313.761C277.734 311.833 279.262 310.349 281.191 309.31C283.12 308.242 285.272 307.708 287.646 307.708C290.02 307.708 292.172 308.242 294.1 309.31C296.029 310.349 297.543 311.833 298.641 313.761C299.769 315.69 300.332 317.916 300.332 320.439C300.332 322.961 299.754 325.187 298.596 327.116C297.469 329.045 295.926 330.543 293.967 331.612C292.038 332.65 289.872 333.17 287.468 333.17ZM287.468 327.739C288.596 327.739 289.649 327.472 290.628 326.938C291.637 326.374 292.439 325.543 293.032 324.445C293.626 323.347 293.922 322.011 293.922 320.439C293.922 318.094 293.299 316.299 292.053 315.052C290.836 313.776 289.337 313.138 287.557 313.138C285.776 313.138 284.278 313.776 283.061 315.052C281.874 316.299 281.28 318.094 281.28 320.439C281.28 322.783 281.859 324.593 283.016 325.869C284.204 327.116 285.687 327.739 287.468 327.739ZM313.362 311.936C314.163 310.631 315.201 309.607 316.478 308.865C317.783 308.123 319.267 307.752 320.929 307.752V314.296H319.282C317.323 314.296 315.84 314.756 314.831 315.676C313.851 316.596 313.362 318.198 313.362 320.483V332.769H307.13V308.108H313.362V311.936Z"
          fill={fillColor}
          className={className}
        />
      </g>

      <defs>
        <clipPath id={clip0}>
          <rect
            width="267.077"
            height="267.077"
            transform="translate(83.4609 83.4614)"
            fill="#fff"
          />
        </clipPath>

        <clipPath id={clip1}>
          <rect
            x="105.729"
            y="105.716"
            width="222.564"
            height="155.795"
            rx="22.2571"
            fill="#fff"
          />
        </clipPath>
      </defs>
    </svg>
  );
};

export default DefaultLogoExhibitor;
